import { invokeApi } from "../../bl_libs/invokeApi";

const authHeaders = () => ({ "x-sh-auth": localStorage.getItem("token") });

// ==================== Connect onboarding ====================
// `returnTo` is the portal path Stripe should send the browser back to, so
// each caller lands where it started rather than on one hardcoded page. The
// server validates it against an allowlist — see utils/safe_return_path.js.
export const create_connect_onboarding_link_api = async (returnTo) =>
  invokeApi({
    path: `api/tutors/connect/onboarding-link`, method: "POST",
    headers: authHeaders(), postData: returnTo ? { returnTo } : {},
  });

export const get_connect_status_api = async () =>
  invokeApi({ path: `api/tutors/connect/status`, method: "GET", headers: authHeaders() });

export const create_school_connect_onboarding_link_api = async (returnTo) =>
  invokeApi({
    path: `api/tutors/school/connect/onboarding-link`, method: "POST",
    headers: authHeaders(), postData: returnTo ? { returnTo } : {},
  });

export const get_school_connect_status_api = async () =>
  invokeApi({ path: `api/tutors/school/connect/status`, method: "GET", headers: authHeaders() });

// ==================== Instructor tutoring profile ====================
export const get_tutoring_profile_api = async () =>
  invokeApi({ path: `api/tutors/profile`, method: "GET", headers: authHeaders() });

export const update_tutoring_profile_api = async (data) =>
  invokeApi({ path: `api/tutors/profile`, method: "PUT", headers: authHeaders(), postData: data });

export const upload_tutoring_profile_photo_api = async (formData) =>
  invokeApi({ path: `api/tutors/profile/photo`, method: "POST", headers: authHeaders(), postData: formData });

// ==================== Availability ====================
export const get_availability_api = async (params = {}) =>
  invokeApi({ path: `api/tutors/availability`, method: "GET", headers: authHeaders(), queryParams: params });

export const add_availability_api = async (data) =>
  invokeApi({ path: `api/tutors/availability`, method: "POST", headers: authHeaders(), postData: data });

export const update_availability_api = async (id, data) =>
  invokeApi({ path: `api/tutors/availability/${id}`, method: "PUT", headers: authHeaders(), postData: data });

export const delete_availability_api = async (id) =>
  invokeApi({ path: `api/tutors/availability/${id}`, method: "DELETE", headers: authHeaders() });

// ==================== Instructor bookings + earnings ====================
export const get_instructor_bookings_api = async (params = {}) =>
  invokeApi({ path: `api/tutors/bookings/instructor`, method: "GET", headers: authHeaders(), queryParams: params });

export const respond_to_booking_api = async (id, decision) =>
  invokeApi({ path: `api/tutors/bookings/${id}/respond`, method: "POST", headers: authHeaders(), postData: { decision } });

export const instructor_cancel_booking_api = async (id) =>
  invokeApi({ path: `api/tutors/bookings/${id}/instructor-cancel`, method: "POST", headers: authHeaders() });

export const admin_refund_booking_api = async (id) =>
  invokeApi({ path: `api/tutors/bookings/${id}/refund`, method: "POST", headers: authHeaders() });

export const get_instructor_earnings_api = async () =>
  invokeApi({ path: `api/tutors/earnings`, method: "GET", headers: authHeaders() });

// ==================== School-wide admin visibility ====================
export const get_school_bookings_api = async (params = {}) =>
  invokeApi({ path: `api/tutors/school/bookings`, method: "GET", headers: authHeaders(), queryParams: params });

// from/to are optional and default to all time. Without them a school can only
// ever see a cumulative total, and cannot ask what a given month actually billed.
export const get_school_tutoring_summary_api = async ({ from, to } = {}) => {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  const q = qs.toString();
  return invokeApi({ path: `api/tutors/school/summary${q ? `?${q}` : ""}`, method: "GET", headers: authHeaders() });
};

export const mark_school_onboarding_seen_api = async () =>
  invokeApi({ path: `api/tutors/school/onboarding-seen`, method: "PUT", headers: authHeaders() });

// ==================== Video ====================
export const get_video_join_info_api = async (bookingId) =>
  invokeApi({ path: `api/tutors/bookings/${bookingId}/video`, method: "GET", headers: authHeaders() });

// Joining a group class. Same response envelope as the 1:1 call above, so the
// video modal takes either without branching on the shape.
export const get_group_video_join_info_api = async (sessionId) =>
  invokeApi({ path: `api/tutors/group-sessions/${sessionId}/video`, method: "GET", headers: authHeaders() });

// One past session, with a playback URL minted for this request and expiring.
// See controllers/tutors/session_library.js — a stored URL is either permanent
// (forwardable by anyone) or expiring (broken when needed), so it is signed on
// demand instead.
export const get_session_record_api = async (bookingId) =>
  invokeApi({ path: `api/tutors/sessions/${bookingId}`, method: "GET", headers: authHeaders() });

// ==================== School Tutors config (Owner/Manager) ====================
export const get_school_tutors_config_api = async () =>
  invokeApi({ path: `api/tutors/school/config`, method: "GET", headers: authHeaders() });

export const update_school_tutors_config_api = async (data) =>
  invokeApi({ path: `api/tutors/school/config`, method: "PUT", headers: authHeaders(), postData: data });

// ==================== Marketplace access requests ====================
export const request_marketplace_access_api = async () =>
  invokeApi({ path: `api/tutors/profile/request-marketplace-access`, method: "POST", headers: authHeaders() });

export const get_pending_access_requests_api = async () =>
  invokeApi({ path: `api/tutors/school/pending-requests`, method: "GET", headers: authHeaders() });

export const respond_marketplace_access_api = async (instructorId, decision) =>
  invokeApi({ path: `api/tutors/instructors/${instructorId}/marketplace-access`, method: "POST", headers: authHeaders(), postData: { decision } });

// ==================== Admin-set instructor rate ====================
export const list_school_instructors_api = async () =>
  invokeApi({ path: `api/tutors/school/instructors`, method: "GET", headers: authHeaders() });

export const update_instructor_rate_api = async (instructorId, hourlyRate) =>
  invokeApi({ path: `api/tutors/instructors/${instructorId}/rate`, method: "PUT", headers: authHeaders(), postData: { hourlyRate } });

// ==================== What an instructor is PAID ====================
// Deliberately a different call from the rate above: that one is what the
// student is charged, this one is what the instructor takes home.
export const update_instructor_compensation_api = async (instructorId, compensation) =>
  invokeApi({ path: `api/tutors/instructors/${instructorId}/compensation`, method: "PUT", headers: authHeaders(), postData: { compensation } });

// ==================== Notifications ====================
export const list_notifications_api = async (unreadOnly = false) =>
  invokeApi({ path: `api/tutors/notifications${unreadOnly ? "?unread=true" : ""}`, method: "GET", headers: authHeaders() });

export const mark_notifications_read_api = async (ids) =>
  invokeApi({ path: `api/tutors/notifications/read`, method: "POST", headers: authHeaders(), postData: ids ? { ids } : {} });

// ==================== Course approvals per instructor (Owner/Manager) ========
export const get_staff_courses_api = async () =>
  invokeApi({ path: `api/tutors/staff/courses`, method: "GET", headers: authHeaders() });

/** data: { courses: string[], canSetOwnRates: boolean|null } — null means "follow the school". */
export const update_staff_courses_api = async (staffId, data) =>
  invokeApi({ path: `api/tutors/staff/${staffId}/courses`, method: "PUT", headers: authHeaders(), postData: data });

/**
 * GET /api/tutors/sessions — paginated, searchable teaching history.
 * Falsy params are dropped so an empty search box does not send `q=`.
 */
export const list_sessions_api = async (params = {}) => {
  const q = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== "" && v !== null && v !== undefined)
  ).toString();
  return invokeApi({
    path: `api/tutors/sessions${q ? `?${q}` : ""}`,
    method: "GET",
    headers: authHeaders(),
  });
};

/**
 * GET /api/tutors/sessions/:id — one session, with everything in it: the
 * signed recording, the transcript as turns, the recap, the to-dos, consent.
 * Resolves a 1:1 booking OR a group class from the same id.
 */
export const get_session_api = async (id) =>
  invokeApi({
    path: `api/tutors/sessions/${id}`,
    method: "GET",
    headers: authHeaders(),
  });

/**
 * POST /api/tutors/sessions/:id/recap — build the recap, or rebuild it.
 *
 * POST rather than GET because it can spend money: a request that bills the
 * account must not be something a browser can issue by prefetching a link.
 * In normal use the recap already exists — it is generated when the transcript
 * lands — so this is the retry and the regenerate.
 */
export const generate_session_recap_api = async (id, force = false) =>
  invokeApi({
    path: `api/tutors/sessions/${id}/recap`,
    method: "POST",
    headers: authHeaders(),
    postData: { force },
  });

/**
 * PATCH /api/tutors/sessions/:id/todos/:index — tick a follow-up off.
 * `text` is echoed as a guard so a page held open across a regenerate cannot
 * tick whatever now occupies that position.
 */
export const set_session_todo_api = async (id, index, done, text) =>
  invokeApi({
    path: `api/tutors/sessions/${id}/todos/${index}`,
    method: "PATCH",
    headers: authHeaders(),
    postData: { done, text },
  });

// ==================== Consolidation pool ====================
// Students who have finished a lesson and need the session that opens the next
// one, without having picked an instructor. Anyone permitted to sign them off
// can take one. Picking one up is an intention, not an appointment — a real
// time still goes through propose_session_api, carrying the request id.

/** GET — { claimable: [...], mine: [...], claimHours } */
export const get_consolidation_requests_api = async () =>
  invokeApi({
    path: "api/tutors/consolidation-requests",
    method: "GET",
    headers: authHeaders(),
  });

export const claim_consolidation_request_api = async (id) =>
  invokeApi({
    path: `api/tutors/consolidation-requests/${id}/claim`,
    method: "POST",
    headers: authHeaders(),
  });

export const release_consolidation_request_api = async (id) =>
  invokeApi({
    path: `api/tutors/consolidation-requests/${id}/release`,
    method: "POST",
    headers: authHeaders(),
  });

/**
 * PATCH /api/tutors/bookings/:id — hand a session over, or change what it
 * covers. Never re-prices: the student authorised an amount already.
 */
export const amend_booking_api = async (id, changes) =>
  invokeApi({
    path: `api/tutors/bookings/${id}`,
    method: "PATCH",
    headers: authHeaders(),
    postData: changes,
  });

/** Who could actually take this session — free at that time, covers the topic. */
export const handover_candidates_api = async (id) =>
  invokeApi({
    path: `api/tutors/bookings/${id}/handover-candidates`,
    method: "GET",
    headers: authHeaders(),
  });

/**
 * GET /api/tutors/bookings/:id/topic-options — what this session may be
 * re-topicked to. Returns a MODE, because the answer is a different shape per
 * course: US is sixteen numbered lessons, UK is subjects containing topics.
 */
export const topic_options_api = async (id) =>
  invokeApi({
    path: `api/tutors/bookings/${id}/topic-options`,
    method: "GET",
    headers: authHeaders(),
  });

// ── the video test bench ────────────────────────────────────────────────
/** POST /api/tutors/video/test — a throwaway room, no booking behind it. */
export const create_test_room_api = async (as = "instructor") =>
  invokeApi({
    path: `api/tutors/video/test?as=${as}`,
    method: "POST",
    headers: authHeaders(),
    postData: {},
  });

export const delete_test_room_api = async (name) =>
  invokeApi({ path: `api/tutors/video/test/${name}`, method: "DELETE", headers: authHeaders() });

/** What the join window WOULD say at an arbitrary moment, without waiting. */
export const preview_join_window_api = async (minutesUntilStart, as) =>
  invokeApi({
    path: `api/tutors/video/test/preview?minutesUntilStart=${minutesUntilStart}&as=${as}`,
    method: "GET",
    headers: authHeaders(),
  });
